import { useMemo } from "react";
import { Autocomplete, Box, Checkbox, Chip, TextField } from "@mui/material";
import { useSnapshot } from "valtio";
import { appStore } from "@store/appStore";

interface OrgOption {
    id: number;
    name: string;
    depth: number;
    hasChildren: boolean;
    parent_id: number | null;
}

interface FlatOrg {
    id: number;
    name: string;
    parent_id: number | null;
}

/**
 * Build an ordered, indented list from the user's permitted orgs.
 * Orgs whose parent is also in the permitted set are nested under them;
 * orgs whose parent is NOT permitted become roots (no greyed-out ancestors).
 */
function buildOrgOptions(orgs: FlatOrg[]): OrgOption[] {
    orgs = orgs.filter((o) => (o.name ?? "").trim() !== "");
    const byId = new Map<number, FlatOrg>();
    for (const o of orgs) byId.set(o.id, o);

    const childrenMap = new Map<number | null, FlatOrg[]>();
    for (const o of orgs) {
        const effectiveParent = o.parent_id != null && byId.has(o.parent_id) ? o.parent_id : null;
        const list = childrenMap.get(effectiveParent) ?? [];
        list.push(o);
        childrenMap.set(effectiveParent, list);
    }
    for (const list of childrenMap.values()) {
        list.sort((a, b) => a.name.localeCompare(b.name));
    }

    const result: OrgOption[] = [];
    function walk(parentId: number | null, depth: number) {
        const kids = childrenMap.get(parentId) ?? [];
        for (const k of kids) {
            const hasChildren = (childrenMap.get(k.id) ?? []).length > 0;
            result.push({
                id: k.id,
                name: k.name,
                depth,
                hasChildren,
                parent_id: k.parent_id ?? null,
            });
            walk(k.id, depth + 1);
        }
    }
    walk(null, 0);
    return result;
}

function descendantsOf(id: number, childrenByParent: Map<number, number[]>): Set<number> {
    const out = new Set<number>();
    const stack = [id];
    while (stack.length) {
        const cur = stack.pop()!;
        for (const child of childrenByParent.get(cur) ?? []) {
            if (out.has(child)) continue;
            out.add(child);
            stack.push(child);
        }
    }
    return out;
}

function ancestorChain(id: number, parentById: Map<number, number | null>): number[] {
    const out: number[] = [];
    let cur = parentById.get(id) ?? null;
    const seen = new Set<number>();
    while (cur != null && !seen.has(cur)) {
        out.push(cur);
        seen.add(cur);
        cur = parentById.get(cur) ?? null;
    }
    return out;
}

interface Props {
    value: number[];
    onChange: (ids: number[]) => void;
    label?: string;
    placeholder?: string;
    size?: "small" | "medium";
    disabled?: boolean;
}

export default function OrganizationMultiPicker({
    value,
    onChange,
    label,
    placeholder = "Search organizations…",
    size = "small",
    disabled = false,
}: Props) {
    const appSnap = useSnapshot(appStore);

    const { options, childrenByParent, parentById } = useMemo(() => {
        const perms = appSnap.loggedInUser?.permissions?.organizations ?? [];
        const flat: FlatOrg[] = perms.map((o: any) => ({
            id: o.id,
            name: o.name,
            parent_id: o.parent_id ?? null,
        }));
        const options = buildOrgOptions(flat);

        const byId = new Map<number, FlatOrg>(flat.map((o) => [o.id, o]));
        const childrenByParent = new Map<number, number[]>();
        const parentById = new Map<number, number | null>();
        for (const o of flat) {
            const effectiveParent = o.parent_id != null && byId.has(o.parent_id) ? o.parent_id : null;
            parentById.set(o.id, effectiveParent);
            if (effectiveParent != null) {
                const arr = childrenByParent.get(effectiveParent) ?? [];
                arr.push(o.id);
                childrenByParent.set(effectiveParent, arr);
            }
        }
        return { options, childrenByParent, parentById };
    }, [appSnap.loggedInUser?.permissions?.organizations]);

    const selectedSet = useMemo(() => new Set(value), [value]);
    const selectedOptions = useMemo(
        () => options.filter((o) => selectedSet.has(o.id)),
        [options, selectedSet]
    );

    function applyToggle(id: number, nowSelected: boolean): number[] {
        const next = new Set(selectedSet);
        const descendants = descendantsOf(id, childrenByParent);

        if (nowSelected) {
            // Add the org and its descendants.
            next.add(id);
            for (const d of descendants) next.add(d);
        } else {
            // Remove the org and its descendants; cascade up: ancestors that previously
            // "contained" this id are now incomplete, so remove them too.
            next.delete(id);
            for (const d of descendants) next.delete(d);
            for (const a of ancestorChain(id, parentById)) {
                if (next.has(a)) next.delete(a);
            }
        }
        return options.filter((o) => next.has(o.id)).map((o) => o.id);
    }

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            size={size}
            disabled={disabled}
            options={options}
            value={selectedOptions}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(opts, state) => {
                const input = state.inputValue.toLowerCase();
                if (!input) return opts;
                const matchingIdxs = new Set<number>();
                for (let i = 0; i < opts.length; i++) {
                    if (opts[i].name.toLowerCase().includes(input)) {
                        matchingIdxs.add(i);
                        let depth = opts[i].depth;
                        for (let j = i - 1; j >= 0 && depth > 0; j--) {
                            if (opts[j].depth < depth) {
                                matchingIdxs.add(j);
                                depth = opts[j].depth;
                            }
                        }
                    }
                }
                return opts.filter((_, idx) => matchingIdxs.has(idx));
            }}
            onChange={(_e, _newValue, reason, details) => {
                if (!details?.option) {
                    if (reason === "clear") onChange([]);
                    return;
                }
                const id = details.option.id;
                const wasSelected = selectedSet.has(id);
                const nowSelected = !wasSelected;
                onChange(applyToggle(id, nowSelected));
            }}
            renderOption={(props, opt) => {
                const checked = selectedSet.has(opt.id);
                return (
                    <Box
                        component="li"
                        {...props}
                        key={opt.id}
                        sx={{
                            pl: `${8 + opt.depth * 20}px !important`,
                            py: "4px !important",
                            fontWeight: opt.hasChildren ? 600 : 400,
                            fontSize: "0.875rem",
                        }}
                    >
                        <Checkbox
                            checked={checked}
                            size="small"
                            sx={{ p: 0.5, mr: 1 }}
                            onMouseDown={(e) => e.preventDefault()}
                        />
                        <span>{opt.name}</span>
                    </Box>
                );
            }}
            renderTags={(tagValue) => {
                const roots = tagValue.filter((o) => {
                    const p = parentById.get(o.id) ?? null;
                    return p == null || !selectedSet.has(p);
                });
                const summary = roots.length === 1 ? roots[0].name : "Multiple orgs selected";
                return <Chip size="small" label={summary} sx={{ ml: 0.5 }} />;
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={selectedOptions.length === 0 ? placeholder : ""}
                    variant="outlined"
                />
            )}
        />
    );
}
